import React, { type ReactNode } from 'react'
import { View } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { Typography } from '../../typography/Typography'
import { variantForStyleId } from '../../typography/variants'
import { badgeContract } from './badge.contract'

const VARIANTS = badgeContract.props.variant.values

export type BadgeVariant = (typeof VARIANTS)[number]

export type BadgeProps = {
    children: ReactNode
    variant?: BadgeVariant
    className?: string
    testID?: string
}

export const BADGE_VARIANT = variantForStyleId(badgeContract.textStyleIds[0])

/**
 * A short status word on a filled pill.
 *
 * The ink is read per variant rather than picked here: Figma inks `critical`
 * with `content/default` and every other variant with `content/inverted`,
 * because the critical fill is the only dark one. Choosing in the component
 * would put that decision somewhere design cannot change it.
 *
 * Hugs its content on both axes, so no width or height is emitted and the pill
 * grows with the word inside it.
 */
const variantFor = (variant: BadgeVariant) => {
    const key = `variant=${variant}`
    const found = (badgeContract.variants as Record<string, { container: string; label: string }>)[
        key
    ]
    if (!found) {
        throw new Error(`aos-ds Badge: the Figma contract has no "${key}"`)
    }
    return found
}

export const Badge = ({ children, variant = 'neutral', className, testID }: BadgeProps) => {
    const { container, label } = variantFor(variant)
    return (
        <View
            className={twMerge('flex-row items-center self-start', container, className)}
            testID={testID ?? `badge-${variant}`}
        >
            <Typography variant={BADGE_VARIANT} className={label} testID='badge-label'>
                {children}
            </Typography>
        </View>
    )
}

Badge.displayName = 'Badge'

export { VARIANTS as BADGE_VARIANTS }
